import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer
} from "react";
import { Layout } from "views/Main/Layouts";
import { generatePath, infiniteNestLink, Link, Switch } from "polyrouter";
import { Loader, NoResultsIndicatorAnimation } from "elements";
import { useTranslation } from "utilities/i18n";
import { handleNullFields } from "utilities/stringUtils";
import {
  getColumns,
  HEADER_ID,
  itemCheckedWithSet,
  sortTableData,
  TableBody,
  TableBulkItemHeader,
  TableBulkItemRow,
  TableHeaderItem,
  TableSearchEdit,
  toggleAll,
  toggleItemWithSet
} from "collections/Table";
import { needs, useGuard } from "rbac";
import { TABLE_COLUMN_STATE, TABLES } from "store/userMeta";
import { useSelector } from "react-redux";
import { State } from "store";
import { generateTableHeaders } from "utilities/tableUtils";
import {
  EntitlementAssignmentType,
  toLicenseBeginsIntSecondsString,
  toLicenseDateLocaleDateString,
  toLicenseExpiryLocaleDateString,
  toLicenseUnlimitedCountString
} from "utilities/licenseServerUtils";
import {
  EntitlementGrouping,
  useEntitlementGroupingList
} from "../useEntitlements";
import { DeleteEntitlementGroupsModal } from "../Delete/DeleteEntitlementGroupsModal";
import { ProtectedLink, ProtectedRoute } from "TsRoleGuards";
import { getProductDetailsLink } from "../../ProductCatalog/View/ProductCatalogDetails";
import { AssignEntitlementGroupingsModal } from "../Assign/AssignEntitlementGroupingsModal";
import { CreateEntitlementModal } from "../Create/CreateEntitlementModal";
import {
  defaultEntitlementGroupingState,
  EntitlementGroupingReducer,
  updateDataLoaded,
  updatePageInfo,
  updateSearch,
  updateSelectedItems,
  updateSort
} from "./EntitlementGroupingListReducer";
import { SortState } from "store/filters";

type EntitlementGroupingRowProps = {
  entitlement: EntitlementGrouping;
  onCheck: (id: string) => void;
  checked: boolean;
  showChecks: boolean;
  shownColumns: Array<string>;
};

export const EntitlementGroupingRow: React.FC<EntitlementGroupingRowProps> = ({
  entitlement,
  onCheck,
  checked,
  showChecks,
  shownColumns
}) => {
  const {
    groupingId,
    productId,
    productName,
    name,
    productVersion,
    activationCode,
    mappingId1,
    mappingId2,
    typeName,
    attributeCount,
    attributeTypeName,
    assignmentTypeName,
    assignedCount,
    assignedProductCount,
    pendingAssignmentCount,
    pendingAssignmentProductCount,
    pendingReleaseCount,
    pendingReleaseProductCount,
    removedCount,
    removedProductCount,
    availableCount,
    begins,
    expires,
    creationTime,
    lastModifiedTime
  } = entitlement;
  const { COLUMNS, HIDDEN } = TABLES.CLARITI_ENTITLEMENTS;

  const getTableContent = (column: string) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.NAME:
        content = (
          <Link
            to={generatePath(
              "/manage/licensing/clariti/entitlements/details/:groupingId",
              { groupingId }
            )}
          >
            {handleNullFields(name)}
          </Link>
        );
        break;
      case COLUMNS.TYPE:
        content = productId ? (
          <Link to={getProductDetailsLink(productId, productVersion)}>
            {handleNullFields(productName)}
          </Link>
        ) : (
          handleNullFields(productName)
        );
        break;
      case COLUMNS.VERSION:
        content = handleNullFields(productVersion);
        break;
      case HIDDEN.LICENSE_TYPE:
        content = handleNullFields(typeName);
        break;
      case COLUMNS.ACTIVATION_CODE:
        content = handleNullFields(activationCode);
        break;
      case HIDDEN.MAPPING_ID1:
        content = handleNullFields(mappingId1);
        break;
      case HIDDEN.MAPPING_ID2:
        content = handleNullFields(mappingId2);
        break;
      case HIDDEN.ASSIGNMENT_TYPE:
        content = handleNullFields(assignmentTypeName);
        break;
      case COLUMNS.ATTRIBUTE_COUNT:
        content = toLicenseUnlimitedCountString(attributeCount);
        break;
      case HIDDEN.ATTRIBUTE_TYPE:
        content = handleNullFields(attributeTypeName);
        break;
      case COLUMNS.ASSIGNED_ATTRIBUTES:
        content = toLicenseUnlimitedCountString(assignedCount);
        break;
      case HIDDEN.ASSIGNED_PRODUCT_COUNT:
        content = toLicenseUnlimitedCountString(assignedProductCount);
        break;
      case HIDDEN.PENDING_ATTRIBUTES:
        content = toLicenseUnlimitedCountString(pendingAssignmentCount);
        break;
      case HIDDEN.PENDING_ASSIGN_PRODUCT_COUNT:
        content = toLicenseUnlimitedCountString(pendingAssignmentProductCount);
        break;
      case HIDDEN.PENDING_RELEASE_ATTRIBUTES:
        content = toLicenseUnlimitedCountString(pendingReleaseCount);
        break;
      case HIDDEN.PENDING_RELEASE_PRODUCT_COUNT:
        content = toLicenseUnlimitedCountString(pendingReleaseProductCount);
        break;
      case COLUMNS.REMOVED_ATTRIBUTES:
        content = toLicenseUnlimitedCountString(removedCount);
        break;
      case HIDDEN.REMOVED_PRODUCT_COUNT:
        content = toLicenseUnlimitedCountString(removedProductCount);
        break;
      case HIDDEN.AVAILABLE_ATTRIBUTES:
        content = toLicenseUnlimitedCountString(availableCount);
        break;
      case HIDDEN.START_DATE:
        content = toLicenseBeginsIntSecondsString(begins);
        break;
      case COLUMNS.EXPIRES:
        content = toLicenseExpiryLocaleDateString(expires);
        break;
      case HIDDEN.CREATION_TIME:
        content = toLicenseDateLocaleDateString(creationTime);
        break;
      case HIDDEN.LAST_MODIFIED_TIME:
        content = toLicenseDateLocaleDateString(lastModifiedTime);
        break;
    }

    return <td key={column}>{content}</td>;
  };

  return (
    <TableBulkItemRow
      data-testid={entitlement.name}
      checked={checked}
      onCheck={() => onCheck(groupingId)}
      showChecks={showChecks}
    >
      {shownColumns.map(getTableContent)}
    </TableBulkItemRow>
  );
};

export const EntitlementGroupingList: React.FC<{
  tenantId: string;
}> = ({ tenantId }) => {
  const { Content } = Layout;
  const { t } = useTranslation();
  const [
    {
      dataLoaded,
      selectedItems,
      sort,
      searchTerm,
      entitlementCount,
      entitlements
    },
    dispatch
  ] = useReducer(EntitlementGroupingReducer, defaultEntitlementGroupingState);

  const polyAdminRequirement = needs("license:PolyWrite");
  const userCanEdit = useGuard(polyAdminRequirement);

  const { NAME: TABLE_NAME, COLUMNS, HIDDEN } = TABLES.CLARITI_ENTITLEMENTS;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });
  const { data, fetching, stale } = useEntitlementGroupingList({
    tenantId: tenantId
  });

  const formattedSearchTerm = searchTerm?.toLowerCase() ?? undefined;

  // Since filtering is handled locally, we will automatically load all data so things work as expected
  const filteredData = !!formattedSearchTerm;

  // Tenant ID switch
  useEffect(() => {
    dispatch(updateDataLoaded(false));
    dispatch(updateSelectedItems([]));
  }, [tenantId]);

  const loading = fetching || stale;
  const entitlementsData = data?.searchResult;

  useEffect(() => {
    if (!loading) {
      dispatch(updateDataLoaded(true));

      if (entitlementsData) {
        dispatch(
          updatePageInfo({
            entitlementCount: entitlementsData.totalCount,
            entitlements: entitlementsData.edges.map(entry => {
              return { ...entry.node };
            })
          })
        );
      }
    }
  }, [loading, entitlementsData]);

  const setSelectedItems = useCallback((items: Array<string>) => {
    dispatch(updateSelectedItems(items));
  }, []);
  const setSort = useCallback((sort: SortState) => {
    dispatch(updateSort(sort));
  }, []);
  const setSearch = useCallback((search?: string) => {
    dispatch(updateSearch(search));
  }, []);

  // Sort entitlements
  sortTableData(entitlements, sort);

  const toggleItem = toggleItemWithSet(
    entitlements.length,
    selectedItems,
    setSelectedItems
  );
  const itemChecked = itemCheckedWithSet(selectedItems);

  // TODO: Aggregate and optimize selection state information
  const selectedEntitlements = useMemo(
    () =>
      selectedItems.reduce((entitlementsList, groupingId) => {
        const entitlementObj = entitlements.find(
          entitlement => entitlement.groupingId === groupingId
        );
        if (entitlementObj) {
          entitlementsList.push(entitlementObj);
        }
        return entitlementsList;
      }, [] as Array<EntitlementGrouping>),
    [selectedItems, entitlements]
  );

  const columnTranslations = {
    [COLUMNS.NAME]: t("NAME"),
    [COLUMNS.TYPE]: t("TYPE"),
    [COLUMNS.VERSION]: t("VERSION_ALT"),
    [HIDDEN.LICENSE_TYPE]: t("LICENSE_TYPE"),
    [COLUMNS.ACTIVATION_CODE]: t("ACTIVATION_KEY"),
    [HIDDEN.MAPPING_ID1]: t("SKU"),
    [HIDDEN.MAPPING_ID2]: t("ORDER_NUMBER"),
    [HIDDEN.ASSIGNMENT_TYPE]: t("ENTITLEMENT_ASSIGNMENT_TYPE"),
    [COLUMNS.ATTRIBUTE_COUNT]: t("ATTRIBUTE_COUNT"),
    [HIDDEN.ATTRIBUTE_TYPE]: t("ATTRIBUTE_TYPE"),
    [COLUMNS.ASSIGNED_ATTRIBUTES]: t("ASSIGNED_ATTRIBUTES"),
    [HIDDEN.ASSIGNED_PRODUCT_COUNT]: t("ENTITLEMENT_ASSIGNED_PRODUCT_COUNT"),
    [HIDDEN.PENDING_ATTRIBUTES]: t("ENTITLEMENT_PENDING_ATTRIBUTES"),
    [HIDDEN.PENDING_ASSIGN_PRODUCT_COUNT]: t(
      "ENTITLEMENT_PENDING_ASSIGN_PRODUCT_COUNT"
    ),
    [HIDDEN.PENDING_RELEASE_ATTRIBUTES]: t(
      "ENTITLEMENT_PENDING_RELEASE_ATTRIBUTES"
    ),
    [HIDDEN.PENDING_RELEASE_PRODUCT_COUNT]: t(
      "ENTITLEMENT_PENDING_RELEASE_PRODUCT_COUNT"
    ),
    [COLUMNS.REMOVED_ATTRIBUTES]: t("REMOVED_ATTRIBUTES"),
    [HIDDEN.REMOVED_PRODUCT_COUNT]: t("ENTITLEMENT_REMOVED_PRODUCT_COUNT"),
    [HIDDEN.AVAILABLE_ATTRIBUTES]: t("AVAILABLE_ATTRIBUTES"),
    [HIDDEN.START_DATE]: t("START_DATE"),
    [COLUMNS.EXPIRES]: t("EXPIRES"),
    [HIDDEN.CREATION_TIME]: t("CREATED"),
    [HIDDEN.LAST_MODIFIED_TIME]: t("LAST_MODIFIED")
  };

  const getTableHeaders = (column: string, title: string) => (
    <TableHeaderItem
      fieldName={column}
      title={title}
      sort={{
        currentSort: sort,
        onSort: setSort
      }}
      key={column}
    />
  );

  const shown = getColumns(shownColumns, columnTranslations);
  const hidden = getColumns(hiddenColumns, columnTranslations);

  const refresh = () => {
    dispatch(updateSelectedItems([]));
  };

  // Can be an expensive operation
  const filteredEntitlements = useMemo(() => {
    return formattedSearchTerm
      ? entitlements.filter(entitlement =>
          entitlement?.name?.toLowerCase().includes(formattedSearchTerm)
        )
      : entitlements;
  }, [formattedSearchTerm, entitlements]);

  const hasSelectedItems = selectedItems.length > 0;
  const hasSingleSelectedIem = selectedItems.length === 1;
  const canAssignEntitlement =
    hasSingleSelectedIem &&
    selectedEntitlements[0].assignmentType !==
      EntitlementAssignmentType.Account;

  const displayCount = filteredData
    ? filteredEntitlements.length
    : entitlementCount;

  return (
    <Content>
      <Loader loading={!dataLoaded}>
        {!loading && entitlementCount === 0 ? (
          <NoResultsIndicatorAnimation title={t("NO_ENTITLEMENTS_FOUND")}>
            <ProtectedLink
              to={infiniteNestLink("/add")}
              className="btn btn-primary"
              data-testid="add-entitlement"
              requirement={polyAdminRequirement}
            >
              {t("ADD_NEW", {
                count: selectedEntitlements.length
              })}
            </ProtectedLink>
          </NoResultsIndicatorAnimation>
        ) : (
          <>
            <div className="table-heading">
              <span className="metric" data-testid="entitlement-count">
                {displayCount}
              </span>
              <span className="title ml-5">
                {t("ENTITLEMENT", { count: displayCount })}
              </span>
            </div>
            <div className="grouping mb-15 table-actions">
              <ProtectedLink
                to={infiniteNestLink("/add")}
                className="btn btn-primary"
                data-testid="add-entitlement"
                requirement={polyAdminRequirement}
              >
                {t("ADD_NEW", {
                  count: selectedEntitlements.length
                })}
              </ProtectedLink>
              <ProtectedLink
                to={canAssignEntitlement ? infiniteNestLink("/assign") : ""}
                className={`btn btn-${userCanEdit ? "secondary" : "primary"} ${
                  canAssignEntitlement ? "" : "disabled"
                }`}
                data-testid="assign-entitlement"
                requirement="license:WriteTenant"
              >
                {t("ASSIGN", {
                  count: selectedEntitlements.length
                })}
              </ProtectedLink>
              <ProtectedLink
                to={hasSelectedItems ? infiniteNestLink("/remove") : ""}
                className={hasSelectedItems ? "btn" : "btn disabled"}
                data-testid="remove-entitlement"
                requirement={polyAdminRequirement}
              >
                {t("REMOVE", {
                  count: selectedEntitlements.length
                })}
              </ProtectedLink>
              <TableSearchEdit
                search={{
                  onSearch: setSearch,
                  searchPlaceholder: t("SEARCH_ENTITLEMENTS")
                }}
                edit={{
                  tableName: TABLE_NAME,
                  shownColumns: shown,
                  hiddenColumns: hidden
                }}
              />
            </div>
            <div className="table-container">
              <table className="table custom-table">
                <TableBulkItemHeader
                  checked={itemChecked(HEADER_ID)}
                  onCheck={() =>
                    toggleAll(
                      entitlements.map(entitlement => entitlement.groupingId),
                      selectedItems,
                      setSelectedItems
                    )
                  }
                  showChecks={!!shownColumns.length}
                >
                  {generateTableHeaders(shown, getTableHeaders)}
                </TableBulkItemHeader>
                <TableBody
                  loading={loading}
                  cols={shown.length}
                  accommodateScrollbar
                >
                  {filteredEntitlements.map(entitlement => {
                    return (
                      <EntitlementGroupingRow
                        entitlement={entitlement}
                        key={entitlement.groupingId}
                        onCheck={() => toggleItem(entitlement.groupingId)}
                        checked={itemChecked(entitlement.groupingId)}
                        showChecks={!!shownColumns.length}
                        shownColumns={shown.map(column => column.key)}
                      />
                    );
                  })}
                </TableBody>
              </table>
            </div>
            <Switch nest>
              <ProtectedRoute
                nest
                path="/assign"
                requirement="license:WriteTenant"
              >
                <AssignEntitlementGroupingsModal
                  entitlements={selectedEntitlements}
                  tenantId={tenantId}
                  refresh={refresh}
                />
              </ProtectedRoute>
              <ProtectedRoute
                nest
                path="/remove"
                requirement={polyAdminRequirement}
              >
                <DeleteEntitlementGroupsModal
                  entitlements={selectedEntitlements}
                  refresh={refresh}
                />
              </ProtectedRoute>
            </Switch>
          </>
        )}
        <Switch nest>
          <ProtectedRoute nest path="/add" requirement={polyAdminRequirement}>
            <CreateEntitlementModal tenantId={tenantId} refresh={refresh} />
          </ProtectedRoute>
        </Switch>
      </Loader>
    </Content>
  );
};
